import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useMemo,
  useReducer
} from 'react';
import { isChrome } from 'react-device-detect';
import {
  IPrintContextProps,
  IReactToPrintProps,
  useReactToPrint
} from 'react-to-print';
import { resolveError } from '../formatters/error/resolve-error';
import { DEBUG_PDF_GENERATION, printPageSlow } from './print-actions';
import {
  initialState,
  printSliceActions,
  printSliceReducer
} from './print-slice';

interface ConcussionRxPrintContextProps extends IReactToPrintProps {
  /**
   * Override the default print behaviour
   * @param content
   * @returns
   */
  handlePrintOverride?: (content: ReactNode) => Promise<void>;
}

export const ConcussionRxPrintContextRoot = createContext<
  IPrintContextProps & { isPrinting: boolean; status?: string } & ({ hasError?: false, error?: undefined } | { hasError?: true, error: Error })
>({
  handlePrint: () => !!0,
  isPrinting: false,
  hasError: false,
});

/**
 * Custom print context for the ConcussionRx app
 * This overrides react-to-print when the user is on a non-Chrome browser
 */
export function ConcussionRxPrintContext(props: ConcussionRxPrintContextProps) {
  const [printState, printStateDispatch] = useReducer(
    printSliceReducer,
    initialState
  );
  const handlePrint = useConcussionRxPrintHandler(props, printStateDispatch);

  return (
    <ConcussionRxPrintContextRoot.Provider
      value={{
        handlePrint,
        isPrinting: printState.isLoading,
        status: printState.statusMessage,
        hasError: printState.hasError as any,
        error: printState.error
      }}
      {...props}
    />
  );
}

export const ConcussionRxPrintContextConsumer =
  ConcussionRxPrintContextRoot.Consumer;

export function useConcussionRxPrintHandler(
  args: ConcussionRxPrintContextProps,
  dispatch: Dispatch<any>
) {
  /**
   * Enhance the input arguments
   * The object is memoised so that the object reference is not recreated on every rerender
   */
  const enhancedArgs: Pick<IReactToPrintProps, 'onBeforePrint' | 'onAfterPrint' | 'onPrintError' | 'content'> = useMemo(() => {
    return {
      ...args,
      onBeforePrint() {
        dispatch(printSliceActions.startLoading());
        args.onBeforePrint?.();
      },
      onAfterPrint() {
        dispatch(printSliceActions.setFinished());
        args.onAfterPrint?.();
      },
      onPrintError: (errorLoc, error) => {
        console.debug('[PrintHandler]', errorLoc, error)
        dispatch(printSliceActions.stopLoading());
        dispatch(printSliceActions.setErrorMessage({ error }))
        args.onPrintError?.(errorLoc, error);
      }
    }
  }, [args, dispatch]);

  const print = usePrint(enhancedArgs);
  const { handlePrintOverride} = args;
  /**
   * A function to override print functionality
   * 
   * This uses `args.handlePrintOverride`
   */
  const printOverride = useCallback(async () => {
    try {
      dispatch(printSliceActions.startLoading());
      enhancedArgs.onBeforePrint?.();

      // Run the override
      await handlePrintOverride?.(enhancedArgs.content!());

      dispatch(printSliceActions.setFinished());
    } catch (e) {
      const error = resolveError(e);
      // Pass the error message
      enhancedArgs.onPrintError?.('print-custom' as any, error);
    }

    enhancedArgs.onAfterPrint?.();
  }, [enhancedArgs, handlePrintOverride, dispatch]);

  /**
   * Override print functionality if it is available
   */
  if (args.handlePrintOverride) return printOverride;

  return print;
}

/**
 * This hook conditionally uses the in-browser PDF generation when it is available. Otherwise the PDF generation is delegated to an API
 * @param args
 * @returns
 */
function usePrint(args: IReactToPrintProps) {
  const printWithReactToprint = useReactToPrint({
    ...args
  });

  const printSlow = useCallback(async () => {
    args.onBeforePrint?.();

    await printPageSlow(args.content!());

    args.onAfterPrint?.();
  }, [args]);

  /**
   * Use Chrome's PDF export when it is available
   */
  if (isChrome && !DEBUG_PDF_GENERATION) return printWithReactToprint;

  /**
   *
   * Otherwise use remote PDF generation
   */
  return printSlow;
}
